<template>
  <div class="file-upload-container">
    <el-form 
      :model="form" 
      :rules="rules" 
      ref="uploadForm" 
      label-width="100px"
      class="upload-form"
    >
      <!-- 模块选择区域 -->
      <div class="module-select-section">
        <div class="module-select-header">
          <i class="el-icon-setting"></i>
          <span class="module-select-title">选择上传赛事模块</span>
        </div>
        <div class="form-row module-select-row">
          <el-form-item label="赛事模块" prop="mark" class="form-item-half module-select-item">
            <el-select 
              v-model="form.mark" 
              placeholder="请选择赛事模块"
              style="width: 100%"
              @change="onMarkChange"
              size="medium"
            >
              <el-option label="新文科" :value="8" />
              <el-option label="创新大赛" :value="1" />
              <el-option label="大挑" :value="2" />
              <el-option label="小挑" :value="3" />
              <el-option label="职规赛-求职简历" :value="4" />
              <el-option label="职规赛-生涯发展报告" :value="5" />
              <el-option label="职规赛-就业能力展示" :value="6" />
              <el-option label="职规赛-生涯发展展示" :value="7" />
            </el-select>
          </el-form-item>
          <div class="form-item-half"></div>
        </div>
      </div>

      <!-- 上传表单内容区域 -->
      <div class="upload-content-section">
        <div class="upload-content-header">
          <i class="el-icon-upload"></i>
          <span class="upload-content-title">文件信息</span>
        </div>
        <!-- 第一行：文件名称 + 作者 -->
        <div class="form-row">
          <el-form-item label="文件名称" prop="name" class="form-item-half file-name-input-item">
            <el-input 
              v-model="form.name" 
              placeholder="请输入文件名称"
              clearable
              maxlength="100"
              show-word-limit
            />
          </el-form-item>
          <el-form-item label="作者" prop="author" class="form-item-half">
            <el-input 
              v-model="form.author" 
              placeholder="请输入作者名称"
              clearable
              maxlength="50"
              @focus="onAuthorFocus"
            />
          </el-form-item>
        </div>

        <!-- 第二行：文件类型 + 文件格式 -->
        <div class="form-row">
          <el-form-item label="文件类型" prop="type" class="form-item-half">
            <el-select 
              v-model="form.type" 
              placeholder="请选择文件类型"
              style="width: 100%"
              @change="onTypeChange"
            >
              <el-option label="PPT" :value="1" />
              <el-option label="Word" :value="2" />
            </el-select>
          </el-form-item>
          <el-form-item label="文件格式" prop="format" class="form-item-half">
            <el-select 
              v-model="form.format" 
              placeholder="请选择文件格式"
              style="width: 100%"
            >
              <el-option
                v-for="format in fileFormats"
                :key="format.format"
                :label="format.name"
                :value="format.format"
              />
            </el-select>
          </el-form-item>
        </div>

        <!-- 第三行：页数 + 价格 -->
        <div class="form-row">
          <el-form-item label="页数" prop="page" class="form-item-half">
            <el-input-number 
              v-model="form.page" 
              :min="1" 
              :max="9999"
              placeholder="页数"
              style="width: 100%"
            />
          </el-form-item>
          <el-form-item label="价格" prop="price" class="form-item-half">
            <el-input-number 
              v-model="form.price" 
              :min="0" 
              :max="9999.99"
              :precision="2"
              placeholder="价格"
              style="width: 100%"
            />
          </el-form-item>
        </div>

        <!-- 第四行：动态字段（非新文科、非职规赛、非大挑/小挑：动画标识） -->
        <div class="form-row state-row" v-if="form.mark !== 8 && !isCareerModule(form.mark) && form.mark !== 2 && form.mark !== 3">
          <!-- 其他模块：动画标识（职规赛模块不显示） -->
          <el-form-item 
            v-if="!isCareerModule(form.mark) && !isInnovationModule(form.mark)" 
            label="动画标识" 
            prop="animation" 
            required
            class="form-item-half"
          >
            <el-select 
              v-model="form.animation" 
              placeholder="请选择动画标识"
              style="width: 100%"
            >
              <el-option label="无动画" :value="0" />
              <el-option label="带路演版动画" :value="1" />
              <el-option label="带网评版动画" :value="2" />
            </el-select>
          </el-form-item>
        </div>

        <!-- 新文科：类别 + 类型（同一行均匀排布） -->
        <template v-if="form.mark === 8">
          <div class="form-row">
            <!-- 类别 -->
            <el-form-item 
              label="类别" 
              prop="categoryId" 
              class="form-item-half"
            >
              <el-select 
                v-model="form.categoryId" 
                placeholder="请选择分类类别"
                style="width: 100%"
                @change="onCategoryChange"
              >
                <el-option
                  v-for="category in categories"
                  :key="category.id"
                  :label="category.name"
                  :value="category.id"
                />
              </el-select>
            </el-form-item>
            <!-- 类型 -->
            <el-form-item label="类型" prop="subcategoryId" class="form-item-half">
              <el-select 
                v-model="form.subcategoryId" 
                placeholder="请选择分类类型"
                style="width: 100%"
              >
                <el-option
                  v-for="subcategory in subcategories"
                  :key="subcategory.id"
                  :label="subcategory.name"
                  :value="subcategory.id"
                />
              </el-select>
            </el-form-item>
          </div>
        </template>

        <!-- 其他模块：标签选择（动态显示） -->
        <template v-else>
          <!-- 标签行1：标签1 + 标签2（如果tag1显示）或 标签2 + 标签3（如果tag1不显示） -->
          <div class="form-row" v-if="hasTag(1) ? (hasTag(1) || hasTag(2)) : (hasTag(2) || hasTag(3))">
            <el-form-item 
              v-if="hasTag(1)"
              :label="getTagLabel(1)" 
              :prop="isTagRequired(1) ? 'tag1Id' : ''" 
              :required="isTagRequired(1)"
              class="form-item-half"
            >
              <el-select 
                v-model="form.tag1Id" 
                :placeholder="getTagPlaceholder(1)"
                style="width: 100%"
                :clearable="!isTagRequired(1)"
              >
                <el-option
                  v-for="tag in tag1Options"
                  :key="tag.id"
                  :label="tag.name"
                  :value="tag.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item 
              v-if="hasTag(2)"
              :label="getTagLabel(2)" 
              :prop="isTagRequired(2) ? 'tag2Id' : ''" 
              :required="isTagRequired(2)"
              class="form-item-half"
            >
              <el-select 
                v-model="form.tag2Id" 
                :placeholder="getTagPlaceholder(2)"
                style="width: 100%"
                :clearable="!isTagRequired(2)"
              >
                <el-option
                  v-for="tag in tag2Options"
                  :key="tag.id"
                  :label="tag.name"
                  :value="tag.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item 
              v-if="!hasTag(1) && hasTag(3)"
              :label="getTagLabel(3)" 
              :prop="isTagRequired(3) ? 'tag3Id' : ''" 
              :required="isTagRequired(3)"
              class="form-item-half"
            >
              <el-select 
                v-model="form.tag3Id" 
                :placeholder="getTagPlaceholder(3)"
                style="width: 100%"
                :clearable="!isTagRequired(3)"
              >
                <el-option
                  v-for="tag in tag3Options"
                  :key="tag.id"
                  :label="tag.name"
                  :value="tag.id"
                />
              </el-select>
            </el-form-item>
            <div v-if="!hasTag(1) && hasTag(2) && !hasTag(3)" class="form-item-half"></div>
            <div v-if="hasTag(1) && !hasTag(2)" class="form-item-half"></div>
          </div>
          
          <!-- 标签行2：标签3 + 标签4（如果tag1显示）或 标签4 + 标签5（如果tag1不显示且tag5存在） -->
          <div class="form-row" v-if="hasTag(1) ? (hasTag(3) || hasTag(4)) : (hasTag(4) || hasTag(5))">
            <el-form-item 
              v-if="hasTag(1) && hasTag(3)"
              :label="getTagLabel(3)" 
              :prop="isTagRequired(3) ? 'tag3Id' : ''" 
              :required="isTagRequired(3)"
              class="form-item-half"
            >
              <el-select 
                v-model="form.tag3Id" 
                :placeholder="getTagPlaceholder(3)"
                style="width: 100%"
                :clearable="!isTagRequired(3)"
              >
                <el-option
                  v-for="tag in tag3Options"
                  :key="tag.id"
                  :label="tag.name"
                  :value="tag.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item 
              v-if="hasTag(4)"
              :label="getTagLabel(4)" 
              :prop="isTagRequired(4) ? 'tag4Id' : ''" 
              :required="isTagRequired(4)"
              class="form-item-half"
            >
              <el-select 
                v-model="form.tag4Id" 
                :placeholder="getTagPlaceholder(4)"
                style="width: 100%"
                :clearable="!isTagRequired(4)"
              >
                <el-option
                  v-for="tag in tag4Options"
                  :key="tag.id"
                  :label="tag.name"
                  :value="tag.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item 
              v-if="!hasTag(1) && hasTag(5)"
              :label="getTagLabel(5)" 
              :prop="isTagRequired(5) ? 'tag5Id' : ''" 
              :required="isTagRequired(5)"
              class="form-item-half"
            >
              <el-select 
                v-model="form.tag5Id" 
                :placeholder="getTagPlaceholder(5)"
                style="width: 100%"
                :clearable="!isTagRequired(5)"
              >
                <el-option
                  v-for="tag in tag5Options"
                  :key="tag.id"
                  :label="tag.name"
                  :value="tag.id"
                />
              </el-select>
            </el-form-item>
            <div v-if="hasTag(1) && hasTag(3) && !hasTag(4)" class="form-item-half"></div>
            <div v-if="!hasTag(1) && hasTag(4) && !hasTag(5)" class="form-item-half"></div>
          </div>
          
          <!-- 标签行3：标签5（如果tag1显示且tag5存在） -->
          <div class="form-row" v-if="hasTag(1) && hasTag(5)">
            <el-form-item 
              :label="getTagLabel(5)" 
              :prop="isTagRequired(5) ? 'tag5Id' : ''" 
              :required="isTagRequired(5)"
              class="form-item-half"
            >
              <el-select 
                v-model="form.tag5Id" 
                :placeholder="getTagPlaceholder(5)"
                style="width: 100%"
                :clearable="!isTagRequired(5)"
              >
                <el-option
                  v-for="tag in tag5Options"
                  :key="tag.id"
                  :label="tag.name"
                  :value="tag.id"
                />
              </el-select>
            </el-form-item>
            <div class="form-item-half"></div>
          </div>
        </template>

       <!-- 大挑：主赛道和专项赛道（二选一，放在文件描述上方） -->
       <div class="form-row" v-if="form.mark === 2">
         <el-form-item 
           label="主赛道" 
           prop="tag1Id" 
           class="form-item-half"
           :class="{ 'is-error': daTiaoTagError }"
         >
           <el-select 
             v-model="form.tag1Id" 
             placeholder="请选择主赛道"
             style="width: 100%"
             clearable
             :disabled="!!form.tag2Id"
             @change="onDaTiaoTag1Change"
           >
             <el-option
               v-for="tag in tag1Options"
               :key="tag.id"
               :label="tag.name"
               :value="tag.id"
             />
           </el-select>
         </el-form-item>
         <el-form-item 
           label="专项赛道" 
           prop="tag2Id" 
           class="form-item-half"
           :class="{ 'is-error': daTiaoTagError }"
         >
           <el-select 
             v-model="form.tag2Id" 
             placeholder="请选择专项赛道"
             style="width: 100%"
             clearable
             :disabled="!!form.tag1Id"
             @change="onDaTiaoTag2Change"
           >
             <el-option
               v-for="tag in tag2Options"
               :key="tag.id"
               :label="tag.name"
               :value="tag.id"
             />
           </el-select>
         </el-form-item>
       </div>

       <!-- 小挑：竞赛组别 + 动画标识（同一行，放在文件描述上方） -->
       <div class="form-row" v-if="form.mark === 3">
         <el-form-item 
           label="竞赛组别" 
           prop="tag1Id" 
           required
           class="form-item-half"
         >
           <el-select 
             v-model="form.tag1Id" 
             placeholder="请选择竞赛组别"
             style="width: 100%"
             :clearable="false"
           >
             <el-option
               v-for="tag in tag1Options"
               :key="tag.id"
               :label="tag.name"
               :value="tag.id"
             />
           </el-select>
         </el-form-item>
         <el-form-item 
           label="动画标识" 
           prop="animation" 
           required
           class="form-item-half"
         >
           <el-select 
             v-model="form.animation" 
             placeholder="请选择动画标识"
             style="width: 100%"
           >
             <el-option label="无动画" :value="0" />
             <el-option label="带路演版动画" :value="1" />
             <el-option label="带网评版动画" :value="2" />
           </el-select>
         </el-form-item>
       </div>

       <!-- 文件描述 + 状态（固定和描述同行左右排列） -->
       <div class="form-row state-row">
         <el-form-item label="文件描述" prop="description" class="form-item-half file-description-input-item">
           <el-input 
             v-model="form.description" 
             placeholder="请输入文件描述"
             maxlength="100"
             clearable
             show-word-limit
           />
         </el-form-item>
         <el-form-item label="状态" prop="state" class="form-item-half state-switch-item">
           <div class="state-switch-content">
             <el-switch 
               v-model="form.state"
               :active-value="1"
               :inactive-value="0"
               active-color="#67c23a"
               inactive-color="#e4e7ed"
             />
             <span class="state-switch-text" :class="{ 'is-active': form.state === 1 }">
               {{ form.state === 1 ? '正常' : '下架' }}
             </span>
           </div>
         </el-form-item>
       </div>

       <!-- 大挑：动画标识（放在文件描述下方） -->
       <div class="form-row" v-if="form.mark === 2">
         <el-form-item 
           label="动画标识" 
           prop="animation" 
           required
           class="form-item-half"
         >
           <el-select 
             v-model="form.animation" 
             placeholder="请选择动画标识"
             style="width: 100%"
           >
             <el-option label="无动画" :value="0" />
             <el-option label="带路演版动画" :value="1" />
             <el-option label="带网评版动画" :value="2" />
           </el-select>
         </el-form-item>
         <div class="form-item-half"></div>
       </div>

       <!-- 职规赛：职业方向（放在文件描述下方，所有职规赛模块mark=4,5,6,7都显示） -->
       <div class="form-row" v-if="isCareerModule(form.mark) && hasCareerDirectionTag()">
         <el-form-item 
           :label="getTagLabel(1)" 
           prop="tag1Id" 
           required
           class="form-item-half career-tag-item"
         >
           <el-select 
             v-model="form.tag1Id" 
             :placeholder="getTagPlaceholder(1)"
             style="width: 100%"
             :clearable="false"
           >
             <el-option
               v-for="tag in tag1Options"
               :key="tag.id"
               :label="tag.name"
               :value="tag.id"
             />
           </el-select>
         </el-form-item>
         <div class="form-item-half"></div>
       </div>

       <!-- 创新大赛：动画标识（放在文件描述下方，仅非 Word 文档时显示） -->
       <div class="form-row" v-if="isInnovationModule(form.mark) && !isInnovationWord()">
         <el-form-item 
           label="动画标识" 
           prop="animation" 
           required
           class="form-item-half"
         >
           <el-select 
             v-model="form.animation" 
             placeholder="请选择动画标识"
             style="width: 100%"
           >
             <el-option label="无动画" :value="0" />
             <el-option label="带路演版动画" :value="1" />
             <el-option label="带网评版动画" :value="2" />
           </el-select>
         </el-form-item>
         <div class="form-item-half"></div>
       </div>

       <!-- 备注信息（仅非新文科模块且非职规赛模块） -->
       <div class="form-row" v-if="form.mark !== 8 && !isCareerModule(form.mark)">
         <el-form-item label="备注信息" prop="remark" class="form-item-description file-remark-input-item">
           <el-input 
             v-model="form.remark" 
             type="textarea" 
             :rows="2"
             placeholder="请输入备注信息（可选）"
             maxlength="500"
             show-word-limit
           />
         </el-form-item>
       </div>


      <!-- 第七行：文件上传（左侧上传控件，右侧文件信息） -->
      <div class="form-row file-upload-row">
        <!-- 左侧：文件上传 -->
        <el-form-item label="文件上传" prop="file" class="form-item-half">
          <div class="upload-wrapper">
            <div class="upload-box">
              <el-upload
                ref="upload"
                :action="uploadUrl"
                :auto-upload="false"
                :show-file-list="false"
                :file-list="fileList"
                :before-upload="beforeUpload"
                :on-change="onFileChange"
                :on-remove="onFileRemove"
                :limit="1"
                :accept="acceptedFileTypes"
                class="upload-dragger"
              >
                <div class="upload-content">
                  <i class="el-icon-upload upload-icon"></i>
                  <div class="upload-tip-text">
                    支持 {{ acceptedFileTypes }} 格式<br>
                    文件大小不超过 100MB
                  </div>
                  <el-button 
                    type="primary" 
                    size="small" 
                    class="upload-btn"
                    @click.stop="handleUploadButtonClick"
                  >
                    {{ selectedFile ? '重新选择' : '选择文件' }}
                  </el-button>
                </div>
              </el-upload>
            </div>
          </div>
        </el-form-item>
        <!-- 右侧：文件信息展示（无文件时显示提示） -->
        <el-form-item class="form-item-half file-info-item">
          <div class="file-info-card" :class="{ 'is-placeholder': !selectedFile }">
            <template v-if="selectedFile">
              <div class="file-info-name" :title="selectedFile.name">
                {{ selectedFile.name }}
              </div>
              <div class="file-info-meta">
                <span>大小：{{ (selectedFile.size / 1024 / 1024).toFixed(2) }} MB</span>
                <span v-if="form.format">格式：{{ form.format }}</span>
              </div>
              <div class="file-info-actions">
                <el-button 
                  size="small" 
                  type="primary" 
                  plain 
                  class="file-info-action" 
                  @click="triggerFileSelect"
                >
                  重新选择
                </el-button>
                <span class="divider">|</span>
                <el-button 
                  size="small" 
                  type="danger" 
                  plain 
                  class="file-info-action" 
                  @click="removeSelectedFile"
                >
                  移除
                </el-button>
              </div>
            </template>
            <template v-else>
              <div class="file-info-placeholder">
                <i class="el-icon-folder-opened"></i>
                <p>文件信息将显示在此处</p>
                <p class="tip">请选择文件后查看名称、大小等信息</p>
              </div>
            </template>
          </div>
        </el-form-item>
      </div>

       <!-- 第八行：封面图片上传（全宽） -->
       <div class="form-row">
         <el-form-item label="封面图片" prop="cover" class="form-item-full">
           <el-upload
             ref="coverUpload"
             :action="uploadUrl"
             :auto-upload="false"
             :file-list="coverFileList"
             :before-upload="beforeCoverUpload"
             :on-change="onCoverChange"
             :on-remove="onCoverRemove"
             :limit="1"
             :accept="acceptedImageTypes"
             list-type="picture-card"
             class="cover-upload"
           >
             <i class="el-icon-plus"></i>
           </el-upload>
           <div class="upload-tip" style="text-align: left;">支持 JPG、PNG 格式，建议尺寸 300x200px，只能上传一张</div>
         </el-form-item>
       </div>

       <!-- 第九行：预览图片上传（全宽） -->
       <div class="form-row">
         <el-form-item label="预览图片" prop="images" class="form-item-full">
           <el-upload
             ref="imageUpload"
             :action="uploadUrl"
             :auto-upload="false"
             :file-list="imageFileList"
             :before-upload="beforeImageUpload"
             :on-change="onImageChange"
             :on-remove="onImageRemove"
             :limit="10"
             :accept="acceptedImageTypes"
             :multiple="true"
             list-type="picture-card"
             class="image-upload"
           >
             <i class="el-icon-plus"></i>
           </el-upload>
           <div class="upload-tip" style="text-align: left;">支持 JPG、PNG 格式，最多上传 10 张预览图，可多选</div>
         </el-form-item>
       </div>

        <!-- 按钮区域 -->
        <div class="form-actions">
          <el-button @click="handleCancel">取消</el-button>
          <el-button type="primary" @click="handleSubmit" :loading="submitting">
            确定上传
          </el-button>
        </div>
      </div>
    </el-form>

  </div>
</template>

<script>
import api from '@/api/api';

export default {
  name: 'FileUpload',
  props: {
    // 当前模块类型
    module: {
      type: String,
      default: 'all'
    }
  },
  data() {
    return {
      submitting: false,
      form: {
        mark: 8, // 默认新文科，根据props.module初始化
        name: '',
        type: null, // 改为null，不设置默认值
        author: '',
        page: null, // 改为null，不设置默认值
        price: null, // 改为null，不设置默认值
        state: 1,
        format: '',
        categoryId: null,
        subcategoryId: null,
        tag1Id: null,
        tag2Id: null,
        tag3Id: null,
        tag4Id: null,
        tag5Id: null,
        animation: null,
        remark: '',
        description: ''
      },
      rules: {
        mark: [
          { required: true, message: '请选择上传模块', trigger: 'change' }
        ],
        name: [
          { required: true, message: '请输入文件名称', trigger: 'blur' },
          { min: 2, max: 100, message: '文件名称长度在 2 到 100 个字符', trigger: 'blur' }
        ],
        type: [
          { required: true, message: '请选择文件类型', trigger: 'change' }
        ],
        author: [
          { required: true, message: '请输入作者名称', trigger: 'blur' },
          { min: 2, max: 50, message: '作者名称长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        page: [
          { required: true, message: '请输入页数', trigger: 'blur' },
          { type: 'number', min: 1, max: 9999, message: '页数必须在 1 到 9999 之间', trigger: 'blur' }
        ],
        price: [
          { required: true, message: '请输入价格', trigger: 'blur' },
          { type: 'number', min: 0, max: 9999.99, message: '价格必须在 0 到 9999.99 之间', trigger: 'blur' }
        ],
        state: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ],
        format: [
          { required: true, message: '请选择文件格式', trigger: 'change' }
        ],
        categoryId: [
          { required: true, message: '请选择分类类别', trigger: 'change', validator: this.validateCategoryId }
        ],
        subcategoryId: [
          { required: true, message: '请选择分类类型', trigger: 'change', validator: this.validateSubcategoryId }
        ],
        tag1Id: [
          { validator: this.validateTagId, trigger: 'change' }
        ],
        tag2Id: [
          { validator: this.validateTagId, trigger: 'change' }
        ],
        tag3Id: [
          { validator: this.validateTagId, trigger: 'change' }
        ],
        tag4Id: [
          { validator: this.validateTagId, trigger: 'change' }
        ],
        tag5Id: [
          { validator: this.validateTagId, trigger: 'change' }
        ],
        animation: [
          { required: true, message: '请选择动画标识', trigger: 'change', validator: this.validateAnimation }
        ],
        description: [
          { required: true, message: '请输入文件描述', trigger: 'blur' },
          { min: 2, max: 100, message: '文件描述长度在 2 到 100 个字符', trigger: 'blur' }
        ]
      },
      fileList: [],
      coverFileList: [],
      imageFileList: [],
      selectedFile: null, // 选择的文件对象
      selectedCoverFile: null, // 选择的封面图片文件对象
      selectedImageFiles: [], // 选择的预览图片文件对象数组
      categories: [],
      subcategories: [],
      allSubcategories: [],
      fileFormats: [],
      // 标签选项（根据mark动态加载）
      tag1Options: [],
      tag2Options: [],
      tag3Options: [],
      tag4Options: [],
      tag5Options: [],
      // 标签初始化数据（mark和tag的映射关系）
      tagInitData: [],
      // mark对应的tag配置映射 { mark: { tag1: tagName, tag2: tagName, ... } }
      tagConfigMap: {},
      uploadUrl: process.env.VUE_APP_API_BASE_URL + '/xinwenke/admin/upload',
      uploadHeaders: {
        'Authorization': 'Bearer ' + (localStorage.getItem('admin_token_jwt') || localStorage.getItem('admin_token'))
      },
      uploadData: {},
      coverUploadData: {},
      imageUploadData: {},
      acceptedFileTypes: '.ppt,.pptx,.doc,.docx',
      acceptedImageTypes: '.jpg,.jpeg,.png',
      reselectingFile: false,
      daTiaoTagError: false // 大挑标签验证错误状态
    };
  },
  computed: {
    // 文件上传组件专门用于新文科模块
  },
  mounted() {
    // 根据props.module初始化mark值
    if (this.module === 'xinwenke') {
      this.form.mark = 8;
    } else if (this.module === 'internet') {
      this.form.mark = 1;
    } else if (this.module === 'career') {
      this.form.mark = 4; // 默认职规赛-求职简历
    }
    
    this.loadFileFormats();
    // 初始化标签数据（会统一加载分类和标签数据）
    this.initTagData();
    
    // 为上传组件内的图片添加错误处理
    this.$nextTick(() => {
      this.setupImageErrorHandling();
    });
  },
  methods: {
    // 判断是否是职规赛模块（mark=4,5,6,7）
    isCareerModule(mark) {
      return mark >= 4 && mark <= 7;
    },
    
    // 判断职规赛模块是否有职业方向标签（所有职规赛模块都显示职业方向）
    hasCareerDirectionTag() {
      // 所有职规赛模块（mark=4,5,6,7）都显示职业方向
      return this.isCareerModule(this.form.mark);
    },

    // 是否为创新大赛模块
    isInnovationModule(mark) {
      return mark === 1;
    },

    // 创新大赛是否选择了 Word 文档
    isInnovationWord() {
      return this.isInnovationModule(this.form.mark) && this.form.type === 2;
    },
    
    // 加载分类数据
    async loadCategories() {
      try {
        const response = await api.getCategories();
        if (response.data && response.data.code === 200) {
          this.categories = response.data.data || [];
        }
      } catch (error) {
        // 静默处理错误
      }
    },

    // 加载文件格式数据
    async loadFileFormats() {
      try {
        const response = await api.getFileFormats();
        if (response.data && response.data.code === 200) {
          this.fileFormats = response.data.data || [];
        }
      } catch (error) {
        // 静默处理错误
      }
    },

    // 加载子分类数据
    async loadSubcategories() {
      try {
        const response = await api.getSubcategories();
        if (response.data && response.data.code === 200) {
          this.allSubcategories = response.data.data || [];
        }
      } catch (error) {
        // 静默处理错误
      }
    },

    // 模块变化处理
    onMarkChange(mark) {
      // 清空所有标签和分类
      this.form.categoryId = null;
      this.form.subcategoryId = null;
      this.form.tag1Id = null;
      this.form.tag2Id = null;
      this.form.tag3Id = null;
      this.form.tag4Id = null;
      this.form.tag5Id = null;
      this.subcategories = [];
      // 重置大挑标签错误状态
      this.daTiaoTagError = false;
      
      // 根据mark加载对应的标签数据
      this.loadTagsByMark(mark);
      this.onTypeChange(this.form.type);
    },

    // 文件类型变化处理
    onTypeChange(value) {
      if (this.isInnovationModule(this.form.mark)) {
        if (value === 2) {
          // Word 文档仅保留赛道和类型
          this.form.tag2Id = null;
          this.form.tag4Id = null;
          this.form.tag5Id = null;
          this.form.animation = null;
          // 如果类型之前被设置为"无"，重置为空
          if (this.form.tag3Id === 0) {
            this.form.tag3Id = null;
          }
        }
      }
    },

    // 大挑主赛道变化处理（选择主赛道时清空专项赛道）
    onDaTiaoTag1Change(value) {
      if (value) {
        this.form.tag2Id = null;
      }
      this.daTiaoTagError = false;
      // 触发表单验证
      this.$refs.uploadForm.validateField(['tag1Id', 'tag2Id']);
    },

    // 大挑专项赛道变化处理（选择专项赛道时清空主赛道）
    onDaTiaoTag2Change(value) {
      if (value) {
        this.form.tag1Id = null;
      }
      this.daTiaoTagError = false;
      // 触发表单验证
      this.$refs.uploadForm.validateField(['tag1Id', 'tag2Id']);
    },

    // 初始化标签数据（获取所有mark和tag的映射关系）
    async initTagData() {
      try {
        const response = await api.getTagInitData();
        if (response.data && response.data.code === 200) {
          this.tagInitData = response.data.data || [];
          // 构建mark对应的tag配置映射
          this.buildTagConfigMap();
          // 根据初始mark加载标签
          this.loadTagsByMark(this.form.mark);
        }
      } catch (error) {
        console.error('获取标签初始化数据失败:', error);
        // 如果初始化失败，仍然尝试加载当前mark的标签
        this.loadTagsByMark(this.form.mark);
      }
    },

    // 构建tag配置映射
    buildTagConfigMap() {
      this.tagConfigMap = {};
      this.tagInitData.forEach(item => {
        const config = {};
        item.tagList.forEach(tag => {
          // tag值对应tag1Id, tag2Id等（tag=1对应tag1Id，tag=2对应tag2Id）
          // 职规赛的 tag=0（职业方向）映射到 tag1Name，使用 tag1Id
          const tagIndex = tag.tag === 0 ? 1 : tag.tag;
          const tagKey = `tag${tagIndex}Name`;
          config[tagKey] = tag.tagName;
        });
        this.tagConfigMap[item.mark] = config;
      });
      
      // 职规赛模块映射：文件表的 mark=4,5,6,7 对应标签表的 mark=4
      // 如果标签表中有 mark=4 的配置，为 mark=5,6,7 也创建相同的配置
      if (this.tagConfigMap[4]) {
        this.tagConfigMap[5] = { ...this.tagConfigMap[4] };
        this.tagConfigMap[6] = { ...this.tagConfigMap[4] };
        this.tagConfigMap[7] = { ...this.tagConfigMap[4] };
      }
    },

    // 根据mark加载标签数据
    async loadTagsByMark(mark) {
      if (mark === 8) {
        // 新文科：加载分类数据
        await this.loadCategories();
        await this.loadSubcategories();
        // 清空标签选项
        this.tag1Options = [];
        this.tag2Options = [];
        this.tag3Options = [];
        this.tag4Options = [];
        this.tag5Options = [];
      } else {
        // 其他模块：根据tag配置加载标签数据
        const tagConfig = this.tagConfigMap[mark];
        
        // 大挑模块（mark=2）：主赛道(tag=7)和专项赛道(tag=8)二选一
        if (mark === 2) {
          await this.loadTagOptions(2, 7, 'tag1Options'); // 主赛道
          await this.loadTagOptions(2, 8, 'tag2Options'); // 专项赛道
          // 清空其他标签选项
          this.tag3Options = [];
          this.tag4Options = [];
          this.tag5Options = [];
        // 小挑模块（mark=3）：竞赛组别(tag=9)必填
        } else if (mark === 3) {
          await this.loadTagOptions(3, 9, 'tag1Options'); // 竞赛组别
          // 清空其他标签选项
          this.tag2Options = [];
          this.tag3Options = [];
          this.tag4Options = [];
          this.tag5Options = [];
        // 职规赛模块（mark=4,5,6,7）统一加载职业方向标签
        } else if (this.isCareerModule(mark)) {
          // 所有职规赛模块都使用 mark=4 加载职业方向（tag=0），因为它们共享相同的职业方向数据
          await this.loadTagOptions(4, 0, 'tag1Options');
          // 清空其他标签选项
          this.tag2Options = [];
          this.tag3Options = [];
          this.tag4Options = [];
          this.tag5Options = [];
        } else if (tagConfig) {
          // 其他模块：根据tag配置动态加载标签选项
          // tag1对应tag=1，tag2对应tag=2，以此类推
          if (tagConfig.tag1Name) {
            await this.loadTagOptions(mark, 1, 'tag1Options');
          } else {
            this.tag1Options = [];
          }
          
          if (tagConfig.tag2Name) {
            await this.loadTagOptions(mark, 2, 'tag2Options');
          } else {
            this.tag2Options = [];
          }
          
          if (tagConfig.tag3Name) {
            await this.loadTagOptions(mark, 3, 'tag3Options');
            // 如果 tag3 是"类型"，添加"无"选项，避免误选择无法取消
            if (tagConfig.tag3Name === '类型') {
              this.tag3Options = [{ id: 0, name: '无' }, ...this.tag3Options];
            }
          } else {
            this.tag3Options = [];
          }
          
          if (tagConfig.tag4Name) {
            await this.loadTagOptions(mark, 4, 'tag4Options');
          } else {
            this.tag4Options = [];
          }
          
          if (tagConfig.tag5Name) {
            await this.loadTagOptions(mark, 5, 'tag5Options');
          } else {
            this.tag5Options = [];
          }
        } else {
          // 如果没有配置，清空所有标签选项
          this.tag1Options = [];
          this.tag2Options = [];
          this.tag3Options = [];
          this.tag4Options = [];
          this.tag5Options = [];
        }
      }
    },

    // 加载标签选项（调用查询接口）
    async loadTagOptions(mark, tag, targetProperty) {
      try {
        const response = await api.getTagList({
          mark: mark,
          tag: tag,
          keyword: ''
        });
        if (response.data && response.data.code === 200) {
          this[targetProperty] = response.data.data || [];
        } else {
          this[targetProperty] = [];
        }
      } catch (error) {
        console.error(`加载标签选项失败 (mark=${mark}, tag=${tag}):`, error);
        this[targetProperty] = [];
      }
    },

    // 获取标签占位符文本（根据tag配置动态获取）
    getTagPlaceholder(tagIndex) {
      const mark = this.form.mark;
      if (mark === 8) {
        // 新文科不需要标签占位符
        return '';
      }
      
      // 大挑模块的标签
      if (mark === 2) {
        if (tagIndex === 1) return '请选择主赛道';
        if (tagIndex === 2) return '请选择专项赛道';
        return '';
      }
      
      // 小挑模块的标签
      if (mark === 3) {
        if (tagIndex === 1) return '请选择竞赛组别';
        return '';
      }
      
      // 职规赛模块的 tag1 固定为"职业方向"
      if (this.isCareerModule(mark) && tagIndex === 1) {
        return '请选择职业方向';
      }
      
      const tagConfig = this.tagConfigMap[mark];
      if (tagConfig) {
        const tagName = tagConfig[`tag${tagIndex}Name`];
        if (tagName) {
          return `请选择${tagName}`;
        }
      }
      
      return `请选择标签${tagIndex}`;
    },

    // 获取标签显示名称（用于label）
    getTagLabel(tagIndex) {
      const mark = this.form.mark;
      if (mark === 8) {
        return '';
      }
      
      // 大挑模块的标签
      if (mark === 2) {
        if (tagIndex === 1) return '主赛道';
        if (tagIndex === 2) return '专项赛道';
        return '';
      }
      
      // 小挑模块的标签
      if (mark === 3) {
        if (tagIndex === 1) return '竞赛组别';
        return '';
      }
      
      // 职规赛模块的 tag1 固定为"职业方向"
      if (this.isCareerModule(mark) && tagIndex === 1) {
        return '职业方向';
      }
      
      const tagConfig = this.tagConfigMap[mark];
      if (tagConfig) {
        const tagName = tagConfig[`tag${tagIndex}Name`];
        if (tagName) {
          return tagName;
        }
      }
      
      return `标签${tagIndex}`;
    },

    // 判断是否有该标签（创新赛不显示类别，职规赛的tag1在状态行显示，大挑/小挑有专门的表单区域）
    hasTag(tagIndex) {
      if (this.form.mark === 8) {
        return false;
      }
      // 大挑和小挑有专门的表单区域，不在通用标签区域显示
      if (this.form.mark === 2 || this.form.mark === 3) {
        return false;
      }
      const tagConfig = this.tagConfigMap[this.form.mark];
      if (tagConfig) {
        const tagName = tagConfig[`tag${tagIndex}Name`];
        // 创新赛（mark=1）不显示"类别"
        if (this.form.mark === 1 && tagName === '类别') {
          return false;
        }
        // 创新赛 Word 文档仅显示赛道和类型
        if (this.isInnovationWord() && tagName && !['赛道', '类型'].includes(tagName)) {
          return false;
        }
        // 职规赛模块的tag1（职业方向）在状态行显示，不在标签选择区域显示
        if (this.isCareerModule(this.form.mark) && tagIndex === 1 && tagName === '职业方向') {
          return false;
        }
        return !!tagName;
      }
      return false;
    },

    // 判断标签是否必填（赛道、组别、风格、动画必填，类型可选）
    isTagRequired(tagIndex) {
      if (this.form.mark === 8) {
        return false;
      }
      
      // 职规赛模块的 tag1（职业方向）必填
      if (this.isCareerModule(this.form.mark) && tagIndex === 1) {
        return true;
      }
      
      const tagConfig = this.tagConfigMap[this.form.mark];
      if (!tagConfig) {
        return false;
      }
      const tagName = tagConfig[`tag${tagIndex}Name`];
      if (!tagName) {
        return false;
      }
      // 必选标签：赛道、组别、风格、动画、职业方向
      const requiredTags = ['赛道', '组别', '风格', '动画', '职业方向'];
      if (requiredTags.includes(tagName)) {
        return true;
      }
      if (tagName === '类型' && this.isInnovationWord()) {
        return true;
      }
      return false;
    },

    // 验证标签（根据tagName判断是否必填）
    validateTagId(rule, value, callback) {
      const fieldName = rule.field; // tag1Id, tag2Id, tag3Id, tag4Id
      const tagIndex = parseInt(fieldName.replace('tag', '').replace('Id', ''));
      
      if (this.form.mark === 8) {
        // 新文科不需要验证标签
        callback();
        return;
      }
      
      // 大挑模块（mark=2）：主赛道和专项赛道必选其一，且不能都选
      if (this.form.mark === 2) {
        if (tagIndex === 1 || tagIndex === 2) {
          const hasTag1 = !!this.form.tag1Id;
          const hasTag2 = !!this.form.tag2Id;
          
          if (!hasTag1 && !hasTag2) {
            // 都没选，报错
            this.daTiaoTagError = true;
            callback(new Error('主赛道和专项赛道必选其一'));
          } else if (hasTag1 && hasTag2) {
            // 都选了，报错
            this.daTiaoTagError = true;
            callback(new Error('主赛道和专项赛道只能选择其一'));
          } else {
            // 选了一个，通过
            this.daTiaoTagError = false;
            callback();
          }
        } else {
          callback();
        }
        return;
      }
      
      // 小挑模块（mark=3）：竞赛组别必填
      if (this.form.mark === 3 && tagIndex === 1) {
        if (!value) {
          callback(new Error('请选择竞赛组别'));
        } else {
          callback();
        }
        return;
      }
      
      // 职规赛模块的 tag1（职业方向）必填
      if (this.isCareerModule(this.form.mark) && tagIndex === 1) {
        if (!value) {
          callback(new Error('请选择职业方向'));
        } else {
          callback();
        }
        return;
      }
      
      const tagConfig = this.tagConfigMap[this.form.mark];
      if (!tagConfig) {
        callback();
        return;
      }
      
      const tagName = tagConfig[`tag${tagIndex}Name`];
      if (!tagName) {
        // 如果没有配置该标签，不需要验证
        callback();
        return;
      }
      
      // 必选标签：赛道、组别、风格、动画、职业方向
      const requiredTags = ['赛道', '组别', '风格', '动画', '职业方向'];
      let isRequired = requiredTags.includes(tagName);
      if (!isRequired && tagName === '类型' && this.isInnovationWord()) {
        isRequired = true;
      }
      
      if (isRequired && !value) {
        callback(new Error(`请选择${tagName}`));
      } else {
        callback();
      }
    },

    // 验证动画标识（必填，仅非新文科模块且非职规赛模块）
    validateAnimation(rule, value, callback) {
      if (this.form.mark !== 8 && !this.isCareerModule(this.form.mark) && !this.isInnovationWord()) {
        // 动画标识是必填的，但允许值为0（无动画）
        if (value === null || value === undefined) {
          callback(new Error('请选择动画标识'));
        } else {
          callback();
        }
      } else {
        callback();
      }
    },

    // 分类变化处理
    onCategoryChange(categoryId) {
      this.form.subcategoryId = null;
      this.subcategories = [];
      
      if (categoryId) {
        this.subcategories = this.allSubcategories.filter(sub => sub.categoryId === categoryId);
      }
    },

    // 验证大分类（仅新文科必填）
    validateCategoryId(rule, value, callback) {
      if (this.form.mark === 8 && !value) {
        callback(new Error('请选择分类类别'));
      } else {
        callback();
      }
    },

    // 验证子分类（仅新文科必填）
    validateSubcategoryId(rule, value, callback) {
      if (this.form.mark === 8 && !value) {
        callback(new Error('请选择分类类型'));
      } else {
        callback();
      }
    },

    // 作者输入框获得焦点时的处理
    onAuthorFocus() {
      // 移除原有的默认提示处理，因为不再设置默认提示值
    },

    // 文件上传前处理
    beforeUpload(file) {
      const isValidType = this.acceptedFileTypes.split(',').some(type => 
        file.name.toLowerCase().endsWith(type.replace('.', ''))
      );
      
      if (!isValidType) {
        this.$message.error('文件格式不支持！');
        return false;
      }
      
      const isLt100M = file.size / 1024 / 1024 < 100;
      if (!isLt100M) {
        this.$message.error('文件大小不能超过 100MB！');
        return false;
      }
      
      return true;
    },

    // 文件选择变化
    onFileChange(file, fileList) {
      if (file.status === 'ready') {
        // 保存文件对象用于后续上传
        this.selectedFile = file.raw;
        this.reselectingFile = false;
        
        // 自动填充文件信息
        this.autoFillFileInfo(file.raw);
        
      }
    },

    // 文件移除
    onFileRemove() {
      if (this.reselectingFile) {
        return;
      }
      this.selectedFile = null;
      this.form.fileUrl = '';
      // 清空自动填充的文件名（文件名通常是从文件自动填充的）
      // 保留文件类型和格式，因为用户可能已经手动选择过
      this.form.name = '';
      this.form.description = '';
    },

    // 自动填充文件信息
    autoFillFileInfo(file) {
      const fileName = file.name;
      
      // 始终更新文件名（去掉扩展名）为新选择的文件名
      const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
      this.form.name = nameWithoutExt;
      
      // 根据文件扩展名自动设置文件格式和类型
      const fileExtension = fileName.toLowerCase().split('.').pop();
      
      // 设置文件格式：需要在 fileFormats 中查找匹配的格式
      const matchedFormat = this.fileFormats.find(f => 
        f.format && f.format.toLowerCase() === fileExtension.toLowerCase()
      );
      if (matchedFormat) {
        this.form.format = matchedFormat.format;
      } else {
        // 如果找不到匹配的格式，直接使用扩展名（兼容处理）
        this.form.format = fileExtension;
      }
      
      // 根据文件扩展名设置文件类型（强制更新）
      switch (fileExtension) {
        case 'ppt':
        case 'pptx':
          this.form.type = 1; // PPT
          break;
        case 'doc':
        case 'docx':
          this.form.type = 2; // Word
          break;
        default:
          // 如果无法识别，保持null，让用户手动选择
          this.form.type = null;
          break;
      }
      
      // 用去除后缀的文件名预填描述字段，便于用户快速提交
      this.form.description = nameWithoutExt;

      this.onTypeChange(this.form.type);
    },

    // 封面图片上传前处理
    beforeCoverUpload(file) {
      const isValidType = this.acceptedImageTypes.split(',').some(type => 
        file.name.toLowerCase().endsWith(type.replace('.', ''))
      );
      
      if (!isValidType) {
        this.$message.error('图片格式不支持！');
        return false;
      }
      
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB！');
        return false;
      }
      
      return true;
    },

    // 封面图片选择变化
    onCoverChange(file, fileList) {
      if (file.status === 'ready') {
        // 保存封面图片文件对象用于后续上传
        this.selectedCoverFile = file.raw;
        
        // 生成预览URL
        this.form.cover = URL.createObjectURL(file.raw);
        
      }
    },

    // 封面图片移除
    onCoverRemove() {
      this.selectedCoverFile = null;
      this.form.cover = '';
    },

    // 预览图片上传前处理
    beforeImageUpload(file) {
      return this.beforeCoverUpload(file);
    },

    // 预览图片选择变化
    onImageChange(file, fileList) {
      if (file.status === 'ready') {
        // 保存预览图片文件对象用于后续上传
        if (!this.selectedImageFiles) {
          this.selectedImageFiles = [];
        }
        this.selectedImageFiles.push(file.raw);
        
        // 生成预览URL
        if (!this.form.images) {
          this.form.images = [];
        }
        this.form.images.push(URL.createObjectURL(file.raw));
        
        // 显示成功消息，包含当前已选择的图片数量
        const currentCount = this.selectedImageFiles.length;

      }
    },

    // 预览图片移除
    onImageRemove(file) {
      if (this.form.images) {
        const index = this.form.images.indexOf(file.url);
        if (index > -1) {
          this.form.images.splice(index, 1);
          // 同时移除对应的文件对象
          if (this.selectedImageFiles && this.selectedImageFiles[index]) {
            this.selectedImageFiles.splice(index, 1);
          }
        }
      }
      
      // 显示移除成功消息，包含当前剩余的图片数量
      const remainingCount = this.selectedImageFiles ? this.selectedImageFiles.length : 0;
    },

    // 提交表单
    async handleSubmit() {
      try {
        await this.$refs.uploadForm.validate();
        
        if (!this.selectedFile) {
          this.$message.error('请先选择文件！');
          return;
        }
        
        this.submitting = true;
        
        // 构建FormData，支持文件上传
        const formData = new FormData();
        
        // 基础信息
        formData.append('name', this.form.name);
        formData.append('author', this.form.author);
        formData.append('pageCount', this.form.page);
        formData.append('price', this.form.price);
        formData.append('state', this.form.state);
        formData.append('type', this.form.type);
        formData.append('format', this.form.format);
        formData.append('describes', this.form.description || '');
        
        // 根据mark选择不同的上传接口
        if (this.form.mark === 8) {
          // 新文科模块：使用原有接口
          // 分类信息（必填）
          formData.append('categoryId', this.form.categoryId);
          formData.append('subcategoryId', this.form.subcategoryId);
          
          // 文件上传
          if (this.selectedFile) {
            formData.append('file', this.selectedFile);
          }
          
          // 封面图片上传
          if (this.selectedCoverFile) {
            formData.append('coverFile', this.selectedCoverFile);
          }
          
          // 预览图片上传
          if (this.selectedImageFiles && this.selectedImageFiles.length > 0) {
            this.selectedImageFiles.forEach((file) => {
              formData.append('previewFiles', file);
            });
          }
          
          // 调用新文科模块上传接口
          const response = await api.createXinWenKeFile(formData);
          
          if (response.data && response.data.code === 200) {
            this.$message.success('文件上传成功！');
            this.$emit('upload-success');
            this.resetForm();
          } else {
            this.$message.error(response.data?.msg || '文件上传失败！');
          }
        } else {
          // 其他模块：使用通用上传接口
          // 注意：基础信息（name, author, pageCount, price, state, type, format, describes）已在上面添加
          // 这里只添加通用上传接口特有的参数
          formData.append('mark', this.form.mark);
          formData.append('remark', this.form.remark || '');
          
          // 标签信息（根据模块类型和标签名称映射到API期望的参数）
          // API期望的参数映射：
          // 互联网+（mark=1）: tag1Id=组别, tag2Id=赛道, tag3Id=类型, tag4Id=风格
          // 职规赛（mark=4~7）: tag1Id=职业方向
          // 其他模块: 可选
          this.appendTagParams(formData);
          
          // 动画标识（可选）
          if (this.form.animation !== null && this.form.animation !== undefined) {
            formData.append('animation', this.form.animation);
          }
          
          // 文件上传
          if (this.selectedFile) {
            formData.append('file', this.selectedFile);
          }
          
          // 封面图片上传
          if (this.selectedCoverFile) {
            formData.append('coverFile', this.selectedCoverFile);
          }
          
          // 预览图片上传（支持单张和多张）
          if (this.selectedImageFiles && this.selectedImageFiles.length > 0) {
            if (this.selectedImageFiles.length === 1) {
              // 单张预览图
              formData.append('previewFile', this.selectedImageFiles[0]);
            } else {
              // 多张预览图
              this.selectedImageFiles.forEach((file) => {
                formData.append('previewFiles', file);
              });
            }
          }
          
          // 调用通用上传接口
          const response = await api.universalUpload(formData);

          if (response.data && response.data.code === 200) {
            this.$message.success('文件上传成功！');
            this.$emit('upload-success');
            this.resetForm();
          } else {
            this.$message.error(response.data?.msg || '文件上传失败！');
          }
        }
        
      } catch (error) {
        this.$message.error('提交失败！');
      } finally {
        this.submitting = false;
      }
    },

    // 取消上传
    handleCancel() {
      this.$emit('upload-cancel');
    },



    // 根据模块类型和标签名称映射标签参数到API期望的参数
    // API期望的参数映射：
    // 互联网+（mark=1）: tag1Id=组别, tag2Id=赛道, tag3Id=类型, tag4Id=风格
    // 职规赛（mark=4~7）: tag1Id=职业方向
    // 大挑/小挑（mark=2,3）: 可选
    appendTagParams(formData) {
      const mark = this.form.mark;
      const tagConfig = this.tagConfigMap[mark];
      
      if (mark === 1) {
        // 互联网+模块：根据标签名称映射到正确的API参数
        // API期望: tag1Id=组别, tag2Id=赛道, tag3Id=类型, tag4Id=风格
        const tagNameToApiParam = {
          '组别': 'tag1Id',
          '赛道': 'tag2Id',
          '类型': 'tag3Id',
          '风格': 'tag4Id'
        };
        
        // 遍历表单中的标签字段，根据标签名称映射到正确的API参数
        for (let i = 1; i <= 5; i++) {
          const formTagId = this.form[`tag${i}Id`];
          if (formTagId !== null && formTagId !== undefined) {
            const tagName = tagConfig ? tagConfig[`tag${i}Name`] : null;
            const apiParam = tagName ? tagNameToApiParam[tagName] : `tag${i}Id`;
            if (apiParam) {
              formData.append(apiParam, formTagId);
            }
          }
        }
      } else if (this.isCareerModule(mark)) {
        // 职规赛模块：tag1Id=职业方向
        if (this.form.tag1Id !== null && this.form.tag1Id !== undefined) {
          formData.append('tag1Id', this.form.tag1Id);
        }
      } else {
        // 其他模块（大挑/小挑等）：直接传递所有标签
        if (this.form.tag1Id !== null && this.form.tag1Id !== undefined) {
          formData.append('tag1Id', this.form.tag1Id);
        }
        if (this.form.tag2Id !== null && this.form.tag2Id !== undefined) {
          formData.append('tag2Id', this.form.tag2Id);
        }
        if (this.form.tag3Id !== null && this.form.tag3Id !== undefined) {
          formData.append('tag3Id', this.form.tag3Id);
        }
        if (this.form.tag4Id !== null && this.form.tag4Id !== undefined) {
          formData.append('tag4Id', this.form.tag4Id);
        }
        if (this.form.tag5Id !== null && this.form.tag5Id !== undefined) {
          formData.append('tag5Id', this.form.tag5Id);
        }
      }
    },

    // 重置表单
    resetForm() {
      const currentMark = this.form.mark;
      this.$refs.uploadForm.resetFields();
      this.fileList = [];
      this.coverFileList = [];
      this.imageFileList = [];
      this.selectedFile = null;
      this.selectedCoverFile = null;
      this.selectedImageFiles = [];
      this.daTiaoTagError = false; // 重置大挑标签错误状态
      this.form = {
        mark: currentMark, // 保持当前选择的模块
        name: '',
        type: null,
        author: '',
        page: null,
        price: null,
        state: 1,
        format: '',
        categoryId: null,
        subcategoryId: null,
        tag1Id: null,
        tag2Id: null,
        tag3Id: null,
        tag4Id: null,
        tag5Id: null,
        animation: null,
        remark: '',
        description: ''
      };
    },

    // 处理上传按钮点击
    handleUploadButtonClick(event) {
      if (this.selectedFile) {
        // 有文件时，触发重新选择（和右侧重新选择按钮逻辑一致）
        event.preventDefault();
        event.stopPropagation();
        this.triggerFileSelect();
      } else {
        // 没有文件时，手动触发文件选择对话框
        event.preventDefault();
        event.stopPropagation();
        const uploadRef = this.$refs.upload;
        if (uploadRef) {
          // 触发 el-upload 的文件选择
          if (uploadRef.$children && uploadRef.$children.length > 0) {
            const uploadComponent = uploadRef.$children[0];
            if (uploadComponent && typeof uploadComponent.handleClick === 'function') {
              uploadComponent.handleClick();
              return;
            }
          }
          // 退回到直接查找 input 并点击
          const inputEl = uploadRef.$refs?.input || uploadRef.$el?.querySelector('input[type="file"]');
          if (inputEl) {
            inputEl.click();
          }
        }
      }
    },

    // 重新选择文件
    triggerFileSelect() {
      const uploadRef = this.$refs.upload;
      const needClear = !!this.selectedFile && uploadRef && typeof uploadRef.clearFiles === 'function';
      if (needClear) {
        this.reselectingFile = true;
        uploadRef.clearFiles();
      }
      const doClick = (inputEl) => {
        if (inputEl) {
          inputEl.value = null; // 允许选择相同文件
          inputEl.click();
          return true;
        }
        return false;
      };
      
      // 优先使用 Element Upload 内部的 handleClick
      if (uploadRef && uploadRef.$children && uploadRef.$children.length > 0) {
        const uploadComponent = uploadRef.$children[0];
        if (uploadComponent) {
          const internalInput = uploadComponent.$refs?.input;
          if (doClick(internalInput)) {
            return;
          }
          if (typeof uploadComponent.handleClick === 'function') {
            uploadComponent.handleClick();
            return;
          }
        }
      }
      
      // 退回到直接查找 input
      const inputEl = uploadRef?.$refs?.input || uploadRef?.$el?.querySelector('input[type="file"]');
      if (inputEl) {
        doClick(inputEl);
      } else {
        // 下一次 DOM 更新后再尝试
        this.$nextTick(() => {
          const retryInput = uploadRef?.$refs?.input || uploadRef?.$el?.querySelector('input[type="file"]');
          if (retryInput) {
            doClick(retryInput);
          }
        });
      }

      if (needClear) {
        this.$nextTick(() => {
          this.reselectingFile = false;
        });
      }
    },

    // 移除已选择文件
    removeSelectedFile() {
      if (this.$refs.upload && typeof this.$refs.upload.clearFiles === 'function') {
        this.$refs.upload.clearFiles();
      }
      this.onFileRemove();
    },

    // 设置图片错误处理
    setupImageErrorHandling() {
      // 为上传组件内的所有图片添加错误处理
      const handleImageError = (event) => {
        // 静默处理图片加载错误，避免控制台报错
        const img = event.target;
        if (img) {
          // 检查是否是上传组件内的图片
          const isUploadImage = img.closest('.cover-upload, .image-upload');
          if (!isUploadImage) {
            return; // 不是上传组件内的图片，不处理
          }
          
          img.style.display = 'none';
          // 阻止错误事件继续传播
          event.preventDefault();
          event.stopPropagation();
          
          // 可以显示一个占位符
          const parent = img.parentNode;
          if (parent && !parent.querySelector('.image-error-placeholder')) {
            const placeholder = document.createElement('div');
            placeholder.className = 'image-error-placeholder';
            placeholder.innerHTML = '<i class="el-icon-picture"></i>';
            parent.appendChild(placeholder);
          }
        }
      };
      
      // 添加全局错误监听器（作为最后一道防线）
      const globalErrorHandler = (event) => {
        // 检查是否是资源加载错误
        if (event.target && event.target.tagName === 'IMG') {
          const img = event.target;
          const isUploadImage = img.closest('.cover-upload, .image-upload');
          if (isUploadImage) {
            handleImageError(event);
            return true; // 表示已处理
          }
        }
        return false;
      };
      
      // 监听全局错误事件
      window.addEventListener('error', globalErrorHandler, true);
      
      // 使用 MutationObserver 监听新添加的图片
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              // 检查是否是图片元素
              const images = node.querySelectorAll ? node.querySelectorAll('img') : [];
              images.forEach((img) => {
                if (!img.onerror) {
                  img.onerror = handleImageError;
                }
              });
              // 如果节点本身就是图片
              if (node.tagName === 'IMG' && !node.onerror) {
                node.onerror = handleImageError;
              }
            }
          });
        });
      });

      // 观察上传组件容器
      let checkInterval = null;
      this.$nextTick(() => {
        const setupImages = (container) => {
          if (!container) return;
          
          // 为现有图片添加错误处理
          const existingImages = container.querySelectorAll('img');
          existingImages.forEach((img) => {
            if (!img.onerror) {
              img.onerror = handleImageError;
            }
          });
          
          // 开始观察
          observer.observe(container, {
            childList: true,
            subtree: true
          });
        };
        
        const coverUploadEl = this.$refs.coverUpload?.$el;
        const imageUploadEl = this.$refs.imageUpload?.$el;
        
        setupImages(coverUploadEl);
        setupImages(imageUploadEl);
        
        // 定期检查新添加的图片（作为备用方案）
        checkInterval = setInterval(() => {
          if (coverUploadEl) {
            const images = coverUploadEl.querySelectorAll('img:not([data-error-handled])');
            images.forEach((img) => {
              img.setAttribute('data-error-handled', 'true');
              if (!img.onerror) {
                img.onerror = handleImageError;
              }
            });
          }
          if (imageUploadEl) {
            const images = imageUploadEl.querySelectorAll('img:not([data-error-handled])');
            images.forEach((img) => {
              img.setAttribute('data-error-handled', 'true');
              if (!img.onerror) {
                img.onerror = handleImageError;
              }
            });
          }
        }, 500);
      });

      // 在组件销毁时清理所有资源
      this.$once('hook:beforeDestroy', () => {
        window.removeEventListener('error', globalErrorHandler, true);
        observer.disconnect();
        if (checkInterval) {
          clearInterval(checkInterval);
        }
      });
    }
  }
};
</script>

<style scoped>
.file-upload-container {
  padding: 0;
  background: #fff;
  border-radius: 8px;
}

/* 模块选择区域样式 */
.module-select-section {
  background: linear-gradient(135deg, #f5f7fa 0%, #f0f2f5 100%);
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 15px 10px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.module-select-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e4e7ed;
}

.module-select-header i {
  font-size: 18px;
  color: #409eff;
  margin-right: 8px;
}

.module-select-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.module-select-row {
  margin-bottom: 0;
}

.module-select-item {
  margin-bottom: 0;
}

.module-select-item .el-form-item__label {
  font-weight: 600;
  color: #606266;
  font-size: 14px;
}

.module-select-item .el-select {
  max-width: 100%;
}

/* 上传内容区域样式 */
.upload-content-section {
  padding: 0;
  margin-top: 0;
}

.upload-content-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e4e7ed;
}

.upload-content-header i {
  font-size: 18px;
  color: #67c23a;
  margin-right: 8px;
}

.upload-content-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.upload-form {
  max-width: 100%;
}

/* 表单行布局 */
.form-row {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
  align-items: flex-start;
}

.form-item-half {
  flex: 1;
  min-width: 0;
  margin-bottom: 0;
}

.form-item-full {
  width: 100%;
  margin-bottom: 0;
}

.form-item-description {
  width: 100%;
  max-width: 405px;
  margin-bottom: 0;
}

.state-row {
  align-items: center;
}

.state-switch-item {
  margin-left: auto;
}

.state-switch-item .el-form-item__content {
  margin-left: 0;
  display: flex;
  justify-content: flex-end;
}

.state-switch-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.state-switch-text {
  font-weight: 600;
  color: #909399;
}

.state-switch-text.is-active {
  color: #67c23a;
}

/* 表单项样式 */
.upload-form .el-form-item {
  margin-bottom: 0;
}

.upload-form .el-form-item__label {
  font-weight: 500;
  color: #333;
  line-height: 32px;
}

.upload-form .el-form-item__content {
  line-height: 32px;
}

/* 必填字段统一样式 */
.upload-form .el-form-item.is-required .el-form-item__label::before {
  content: '*';
  color: #f56c6c;
  margin-right: 4px;
}

.upload-form .el-form-item.is-required .el-form-item__label {
  font-weight: 600;
}

/* 输入框样式 - 确保宽度一致 */
.upload-form .el-input,
.upload-form .el-select,
.upload-form .el-textarea {
  width: 100%;
  max-width: 305px; /* 与文件上传区域保持一致的最大宽度 */
}

.upload-form .el-input-number {
  width: 100%;
  max-width: 305px; /* 与文件上传区域保持一致的最大宽度 */
}

/* 文件描述输入框特殊处理 */
.form-item-description {
  max-width: 405px; /* 文件描述框稍宽一些 */
}

/* 文件上传信息展示区域 */
.file-upload-row .file-info-item .el-form-item__content {
  margin-left: 0;
  display: flex;
  justify-content: center;
}

.file-info-card {
  max-width: 305px;
  height: 140px;
  padding: 8px 10px;
  border-radius: 6px;
  background-color: #f8f9fb;
  border: 1px solid #e4e7ed;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.file-info-card.is-placeholder {
  justify-content: center;
  align-items: center;
  text-align: center;
  color: #a0a3b1;
  gap: 6px;
}

.file-info-name {
  font-size: 13px;
  color: #303133;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 6px;
  text-align: center;
  width: 100%;
  max-width: 100%;
}

.file-info-meta {
  font-size: 12px;
  color: #909399;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
}

.file-info-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 12px;
  color: #909399;
}

.file-info-action {
  padding: 4px 12px;
  font-size: 12px;
  border-radius: 6px;
}

.file-info-action.el-button--primary.is-plain {
  border-color: #b3d8ff;
  color: #409eff;
  background-color: #f4f9ff;
}

.file-info-action.el-button--primary.is-plain:hover,
.file-info-action.el-button--primary.is-plain:focus {
  color: #2d8cf0;
  border-color: #90c2ff;
  background-color: #e8f3ff;
}

.file-info-action.el-button--danger.is-plain {
  border-color: #ffb8b2;
  color: #f56c6c;
  background-color: #fff3f2;
}

.file-info-action.el-button--danger.is-plain:hover,
.file-info-action.el-button--danger.is-plain:focus {
  color: #e45a5a;
  border-color: #ffa39e;
  background-color: #ffeceb;
}

.file-info-actions .divider {
  color: #dcdfe6;
}

.file-info-placeholder i {
  font-size: 24px;
  color: #c0c4cc;
}

.file-info-placeholder p {
  margin: 0;
  font-size: 12px;
  color: #a0a3b1;
}

.file-info-placeholder .tip {
  font-size: 11px;
  color: #c0c4cc;
}

/* 文件上传区域 */
.upload-wrapper {
  width: 100%;
  max-width: 305px;
  height: 140px;
  position: relative;
}

.upload-box {
  width: 305px;
  height: 140px;
  border: 2px dashed #d9d9d9;
  border-radius: 6px;
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  transition: border-color 0.3s;
  background: #fff;
}

.upload-box:hover {
  border-color: #409eff;
}

.upload-dragger {
  width: 100%;
  height: 100%;
}

::v-deep .upload-dragger .el-upload {
  width: 100%;
  height: 100%;
  display: block;
}

::v-deep .upload-dragger .el-upload-dragger {
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
}

.upload-content {
  width: 100%;
  height: 100%;
  padding: 15px;
  box-sizing: border-box;
  position: relative;
}

.upload-icon {
  position: absolute;
  left: 50%;
  top: 20px;
  transform: translateX(-50%);
  font-size: 28px;
  color: #c0c4cc;
  line-height: 1;
}

.upload-btn {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  white-space: nowrap;
}

.upload-tip-text {
  position: absolute;
  left: 50%;
  bottom: 15px;
  transform: translateX(-50%);
  color: #909399;
  font-size: 12px;
  line-height: 1.6;
  text-align: center;
  width: calc(100% - 30px);
}

/* 文件列表样式优化 */
.upload-dragger .el-upload-list {
  max-width: 100%;
  margin-top: 10px;
}

.upload-dragger .el-upload-list__item {
  max-width: 100%;
  width: auto;
  margin-right: 0;
  margin-bottom: 8px;
  padding: 8px 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.upload-dragger .el-upload-list__item .el-upload-list__item-name {
  flex: 1;
  margin-right: 40px; /* 为删除按钮留出空间 */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: #666;
  max-width: calc(100% - 50px); /* 限制最大宽度 */
}

.upload-dragger .el-upload-list__item .el-upload-list__item-status-label {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: #67c23a;
  color: white;
  padding: 2px 6px;
  border-radius: 2px;
  font-size: 10px;
}

.upload-dragger .el-upload-list__item .el-upload-list__item-delete {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #f56c6c;
  font-size: 14px;
  cursor: pointer;
  z-index: 1;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.upload-dragger .el-upload-list__item .el-upload-list__item-delete:hover {
  color: white;
  background: #f56c6c;
  transform: translateY(-50%) scale(1.1);
}


/* 图片上传区域 */
.cover-upload,
.image-upload {
  width: 100%;
}

.cover-upload .el-upload--picture-card,
.image-upload .el-upload--picture-card {
  width: 80px;
  height: 80px;
  line-height: 80px;
}

.cover-upload .el-upload-list--picture-card .el-upload-list__item,
.image-upload .el-upload-list--picture-card .el-upload-list__item {
  width: 80px;
  height: 80px;
}

/* 图片加载失败时的占位符样式 */
.image-error-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #c0c4cc;
  font-size: 24px;
}

/* 静默处理图片加载错误，避免控制台报错 */
.cover-upload img,
.image-upload img {
  object-fit: cover;
}

/* 隐藏加载失败的图片，避免显示破损图标 */
.cover-upload img[src=""],
.image-upload img[src=""],
.cover-upload img:not([src]),
.image-upload img:not([src]) {
  display: none;
}

.upload-tip {
  color: #909399;
  font-size: 11px;
  margin-top: 6px;
  line-height: 1.4;
  text-align: left;
}

/* 职规赛模块职业方向样式 */
.career-tag-item {
  flex: 1;
  min-width: 0;
}

.career-tag-item .el-form-item__label {
  font-weight: 600;
  color: #333;
}

.career-tag-item .el-select {
  width: 100%;
  max-width: 305px;
}

/* 按钮区域 */
.form-actions {
  text-align: center;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #f0f0f0;
}

.form-actions .el-button {
  min-width: 100px;
  margin: 0 10px;
}

/* 响应式适配 */
@media (max-width: 768px) {
  .file-upload-container {
    padding: 15px;
  }
  
  .form-row {
    flex-direction: column;
    gap: 15px;
  }
  
  .form-item-half {
    width: 100%;
  }
  
  .upload-form .el-form-item__label {
    width: 100%;
    text-align: left;
    margin-bottom: 8px;
  }

  .upload-form .el-form-item__content {
    margin-left: 0;
  }
  
  .form-actions .el-button {
    width: 100%;
    margin: 5px 0;
  }
}

/* 小屏幕适配 */
@media (max-width: 480px) {
  .file-upload-container {
    padding: 10px;
  }
  
  .upload-dragger .el-upload-dragger {
    height: 95px;
  }

  .upload-dragger .el-upload-dragger .el-icon-upload {
    font-size: 22px;
    margin: 12px 0 8px;
  }

  .upload-dragger .el-upload__text {
    font-size: 12px;
  }
  
  /* 移动端宽度调整 */
  .upload-form .el-input,
  .upload-form .el-select,
  .upload-form .el-textarea,
  .upload-form .el-input-number {
    max-width: 100%;
  }

  .upload-wrapper {
    max-width: 100%;
  }
  
  .upload-box {
    width: 100%;
    max-width: 305px;
  }

  .upload-icon {
    font-size: 24px;
    left: 50%;
    top: 15px;
    transform: translateX(-50%);
  }

  .upload-btn {
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 7px 12px;
    font-size: 12px;
  }

  .upload-tip-text {
    font-size: 11px;
    left: 50%;
    bottom: 12px;
    transform: translateX(-50%);
    text-align: center;
    width: calc(100% - 24px);
  }
  
  .form-item-description {
    max-width: 100%;
  }

  /* 移动端文件列表优化 */
  .upload-dragger .el-upload-list__item {
    padding: 6px 10px;
    font-size: 11px;
  }

  .upload-dragger .el-upload-list__item .el-upload-list__item-name {
    font-size: 11px;
    margin-right: 35px; /* 移动端减少右边距 */
    max-width: calc(100% - 45px);
  }

  .upload-dragger .el-upload-list__item .el-upload-list__item-delete {
    font-size: 12px;
    width: 18px;
    height: 18px;
    right: 6px;
  }
}

/* 大挑标签提示样式 */
.tag-hint {
  margin-top: 4px;
}

.tag-hint .hint-text {
  font-size: 12px;
  color: #e6a23c;
}

/* 大挑标签错误状态 */
.form-item-half.is-error :deep(.el-select .el-input__inner) {
  border-color: #f56c6c;
}

/* 文件名称输入框右内边距调整（为字数统计留出空间） */
.file-name-input-item :deep(.el-input--suffix .el-input__inner) {
  padding-right: 80px !important;
}

/* 文件描述输入框右内边距调整（为字数统计留出空间，无清除按钮） */
.file-description-input-item :deep(.el-input__inner) {
  padding-right: 80px !important;
}

/* 备注信息输入框内边距调整（为字数统计留出空间，textarea类型） */
.file-remark-input-item :deep(.el-textarea__inner) {
  padding-right: 80px !important;
  padding-bottom: 30px !important;
}

</style>
